import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  // useEffect,
} from 'react'
import { Box, InputAdornment, Typography } from '@mui/material'
import SelectBox from '../../../../components/ui/SelectBox'
import InputComponent from '../../../../components/ui/InputComponent'
import {
  IOnboardSocialChannelPayload,
  OnboardSocialChannelMethods,
} from '../../../../types/contentCreatorTypes'
import {
  followerOptions,
  socialPlatforms,
} from '../../../../helpers/static-constants'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import {
  selectOnboardSocialChannelsDetail,
  setOnboardSocialChannelsDetail,
} from '../../../../store/influencer/contentCreator'
import { useAppSelector } from '../../../../store'
import { IconChevronDown } from '@tabler/icons-react'

interface SocialChannelsStepProps {
  setIsValidData: (isValid: boolean) => void
  readonly?: boolean
}

const SocialChannelsStep = forwardRef<
  OnboardSocialChannelMethods,
  SocialChannelsStepProps
>(({ setIsValidData, readonly }, ref) => {
  const { dispatch } = useAppUtilities()
  const socialChannels = useAppSelector(selectOnboardSocialChannelsDetail)
  const [usernames, setUsernames] = useState<{ [key: string]: string }>({})
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({})
  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: string
  }>({})

  useEffect(() => {
    const hasAnyInput =
      Object.values(usernames).some((v) => v?.trim()) ||
      Object.values(selectedValues).some((v) => v?.trim())

    const validEntries = Object.entries(usernames).filter(
      ([platform, username]) => username && selectedValues[platform],
    )

    const hasPartialEntry = Object.entries(usernames).some(
      ([platform, username]) =>
        (!!username || !!selectedValues[platform]) &&
        (!username || !selectedValues[platform]),
    )

    const isValid = hasAnyInput && validEntries.length > 0 && !hasPartialEntry

    setIsValidData(isValid)

    const newErrors: { [key: string]: boolean } = {}
    for (const platform of socialPlatforms) {
      newErrors[platform] =
        hasAnyInput &&
        (!!usernames[platform] || !!selectedValues[platform]) &&
        (!usernames[platform] || !selectedValues[platform])
    }

    setFieldErrors(newErrors)
  }, [usernames, selectedValues])

  useEffect(() => {
    onDefault()
  }, [socialChannels])

  const onDefault = (): void => {
    if (socialChannels?.length) {
      const initialUsernames: { [key: string]: string } = {}
      const initialFollowers: { [key: string]: string } = {}

      for (const { type, followers, username } of socialChannels) {
        initialUsernames[type] = username
        initialFollowers[type] = followers
      }

      setUsernames(initialUsernames)
      setSelectedValues(initialFollowers)
    }
  }

  const handleUsernameChange = (platform: string, value: string): void => {
    setUsernames((prev) => ({ ...prev, [platform]: value }))
  }

  const handleSelectChange = (platform: string, value: string): void => {
    setSelectedValues((prev) => ({ ...prev, [platform]: value }))
  }

  const getPayload = (): IOnboardSocialChannelPayload[] => {
    const socialData = Object.entries(usernames)
      .filter(([platform, username]) => username && selectedValues[platform])
      .map(([platform, username]) => ({
        type: platform,
        followers: selectedValues[platform],
        username,
      }))
    if (socialData.length === 0) {
      return []
    }

    dispatch(setOnboardSocialChannelsDetail(socialData))
    return socialData
  }

  const resetForm = (): void => {
    onDefault()
  }

  useImperativeHandle(ref, () => ({
    getPayload,
    resetForm,
  }))

  return (
    <Box>
      {socialPlatforms.map((platform) => {
        const username = usernames[platform] || ''
        const followers = selectedValues[platform] || ''
        const hasValue = !!username && !!followers

        return hasValue || !readonly ? (
          <Box
            key={platform}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            mb={2}
          >
            {readonly ? (
              <>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  px={2}
                  py={1.5}
                  borderRadius={'8px'}
                  bgcolor="grey.50"
                >
                  <Box
                    component="img"
                    src={`/new/images/icons/onboard/${platform}.svg`}
                    alt={`${platform} icon`}
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="subtitle1"> {username}</Typography>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  px={2}
                  py={1.5}
                  borderRadius={'8px'}
                  bgcolor="grey.50"
                >
                  <Typography variant="subtitle1"> {followers}</Typography>
                  <IconChevronDown stroke={2} size={18} />
                </Box>
              </>
            ) : (
              <>
                <InputComponent
                  id={`${platform}-username`}
                  value={username}
                  placeholder="Username"
                  labelWeight={500}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="img"
                          src={`/new/images/icons/onboard/${platform}.svg`}
                          alt={`${platform} icon`}
                        />
                      </InputAdornment>
                    ),
                  }}
                  setValue={(value) => handleUsernameChange(platform, value)}
                  error={!!fieldErrors[platform]}
                />
                <SelectBox
                  disabled={!username.length}
                  id={`${platform}-followers`}
                  value={followers}
                  onChange={(e) =>
                    handleSelectChange(platform, e.target.value as string)
                  }
                  options={followerOptions}
                  error={!!fieldErrors[platform]}
                />
              </>
            )}
          </Box>
        ) : null
      })}
    </Box>
  )
})

SocialChannelsStep.displayName = 'SocialChannelsStep'

export default SocialChannelsStep
